import {makeSecureApiCall} from './api';
import {cast} from '../entities/schemas';
import {getCurrentFirm, getCurrentUser} from "../lib/picos/currentLogin";
import {Result} from "oxide.ts";

const BANK_URL = '/lawpayauth/payment/bank';
const CARD_URL = '/lawpayauth/payment/card';

/**
 * Helper function to process errors from api calls.
 * @param {object} result
 * @param {number} result.status
 * @param {object} result.body
 * @returns {*}
 */
function processResults (result) {
    if (!result || typeof(result) !== 'object') throw new Error('Error 500: Could not process result');

    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}: ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Update information for a contact with LawPay
 *
 * @param {Contact} contact - the contact to update
 * @returns {Promise<null>}
 */
export async function updateContact(contact) {
    if (!contact) throw new Error('updateContact called with no contact specified');

    const result = await makeSecureApiCall(
        `/lawpayauth/contact/${contact.processorContactId}`,
        'PATCH',
        {contact}
    );

    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Start the signup flow for LawPay.
 *
 * Returns the new merchantId, although it is not useful immediately.
 *
 * When the application is accepted/rejected, the server will get an event, which will then kick off another
 * flow on the server, which will update the firm object with the LawPay account information, or will send
 * an alert to the user to let them know that their application was denied.
 *
 * @returns {Promise<{applicationId: string, redirect_uri: string}>}
 */
export async function signup() {
    const result = await makeSecureApiCall('/lawpayauth/signup', 'POST', {
        firm: getCurrentFirm(),
        user: getCurrentUser()
    });

    const status = result.status;

    /**
     * The result will contain a merchant application/id and a redirect_uri
     * @type {{merchant_application: {id: number}, redirect_uri: string}}
     */
    const body = result.body;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return {
            applicationId: body.merchant_application?.id,
            redirect_uri: body.redirect_uri
        };
    }
}

/**
 * Have the server get the lawpay gateway token and put it in the firm record.
 *
 * @returns {Promise<string>}
 */
export async function disconnect() {
    const result = await makeSecureApiCall('/lawpayauth/disconnect', 'POST', {});
    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Have the server get the lawpay gateway token and put it in the firm record.
 *
 * @returns {Promise<LawpayBankAccount[]>}
 */
export async function getAccounts() {
    const result = await makeSecureApiCall('/lawpayauth/accounts', 'GET', {});
    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body.map(o => cast('lawpay', o));
    }
}

/**
 * Have the server get the lawpay gateway token and put it in the firm record.
 *
 * @returns {Promise<LawpayContact[]>}
 */
export async function getContacts() {
    const result = await makeSecureApiCall('/lawpayauth/contacts', 'GET', {});
    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Have the server get the lawpay gateway token and put it in the firm record.
 *
 * @returns {Promise<Object[]>}
 */
export async function getPaymentMethods(contact) {
    if (!contact.processorContactId) return [];

    const result = await makeSecureApiCall('/lawpayauth/contacts/paymentMethods', 'GET', {processorContactId: contact.processorContactId});
    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Update information for a contact with LawPay
 *
 * @param {Contact} contact - the contact to update
 * @param {object} paymentMethod - the payment method to use
 * @returns {Promise<string>}
 */
export async function createPaymentMethod(contact, paymentMethod) {
    return await makeSecureApiCall(
        `/lawpayauth/contact/paymentMethods`,
        'POST',
        {processorContactId: contact.processorContactId, paymentMethod}
    );
}

/**
 * Update information for a contact with LawPay
 *
 * @param {Contact} contact - the contact to update
 * @param {paymentMethod} paymentMethod
 * @returns {Promise<PaymentMethod>}
 */
export async function updatePaymentMethod(contact, paymentMethod) {
    const result = await makeSecureApiCall(
        `/lawpayauth/contact/paymentMethods`,
        'PATCH',
        {processorContactId: contact.processorContactId, paymentMethod}
    );

    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Update information for a contact with LawPay
 *
 * @param {Contact} contact - the contact to update
 * @param {object} paymentMethod
 * @returns {Promise<string>}
 */
export async function deletePaymentMethod(contact, paymentMethod) {
    const result = await makeSecureApiCall(
        `/lawpayauth/contact/${contact.processorContactId}/payment-methods/${paymentMethod.id}`,
        'DELETE',
        {}
    );

    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Have the server get the lawpay gateway token and put it in the firm record.
 *
 * @param {Contact[]} contacts
 * @returns {Promise<string>}
 */
export async function syncContacts(contacts) {
    const result = await makeSecureApiCall('/lawpayauth/syncContacts', 'POST', {contacts});
    const {status, body} = result;

    if (status >= 400) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 * Delete a contact in lawpay (NOT in Outlaw).
 *
 * @param {string} processorContactId
 * @returns {Promise<*>}
 */
export async function deleteContact(processorContactId) {
    const result = await makeSecureApiCall(
        `/lawpayauth/contact/${processorContactId}`,
        'DELETE',
        {}
    );

    const {status, body} = result;

    if (status >= 400 && status !== 404) {
        // Handle HTTP error
        throw new Error(`Error ${status}, ${body}`);
    }
    else {
        return body;
    }
}

/**
 *
 * @param {Contact} contact
 * @returns {Promise<*>}
 */
export async function getContact(contact) {
    const result = await makeSecureApiCall(`/lawpayauth/contacts/${contact.processorContactId}`, 'GET', {});
    return processResults(result);
}

/**
 * Create a payment using the given payment method for the given amount.
 *
 * @param {string} paymentMethodId - payment method ID from the provider
 * @param {number} amount - amount of the transaction.  Debits will be positive numbers, credits will be negative!
 * @param {string} accountId - the account in which to create the charge
 * @returns {Promise<*>}
 */
export async function createPayment(paymentMethodId, amount, accountId): Promise<Result<any, Error>> {
    const result = await makeSecureApiCall(`/lawpayauth/payments`, 'POST', {
        paymentMethodId,
        amount,
        accountId
    });

    return processResults(result);
}

/**
 * Refund (fully) a transaction.
 *
 * @param {string} transactionId
 * @param {number} amount - amount in $.cc
 * @returns {Promise<*>}
 */
export async function refundTransaction(transactionId, amount) {
    const result = await makeSecureApiCall(`/lawpayauth/payments/refund/${transactionId}`, 'POST', {amount});

    return processResults(result);
}

/**
 * Get full details on a transaction.
 *
 * @param {string} transactionId
 * @returns {Promise<*>}
 */
export async function voidTransaction(transactionId) {
    const result = await makeSecureApiCall(`/lawpayauth/payments/void/${transactionId}`, 'POST', {});

    return processResults(result);
}

/**
 * Get full details on a transaction.
 *
 * @param {string} transactionId
 * @returns {Promise<*>}
 */
export async function getTransaction(transactionId) {
    const result = await makeSecureApiCall(`/lawpayauth/payments/${transactionId}`, 'GET', {});

    return processResults(result);
}

/**
 * Get all transactions for the given date range.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<*>}
 */
export async function getTransactions(startDate, endDate) {
    const result = await makeSecureApiCall(`/lawpayauth/payments`, 'GET', {
        startDate: startDate.toISOString(), endDate: endDate.toISOString()
    });

    return processResults(result);
}

/*
export async function listBanks() {
    const result = await makeSecureApiCall(`${BANK_URL}s`, 'GET', {});
    return processResults(result);
}

export async function listCards() {
    const result = await makeSecureApiCall(`${CARD_URL}s`, 'GET', {});
    return processResults(result);
}

export async function storeBank(paymentMethod) {
    const result = await makeSecureApiCall(`${BANK_URL}`, 'POST', paymentMethod.LawPay);
    return processResults(result);
}

export async function storeCard(paymentMethod) {
    const result = await makeSecureApiCall(`${CARD_URL}`, 'POST', paymentMethod.LawPay);
    return processResults(result);
}

export async function createPayment(contact, paymentMethod) {
    const result = await makeSecureApiCall('/lawpayauth/contact/paymentMethods', 'POST', {
        paymentMethod: paymentMethod.LawPay,
        processorContactId: contact.processorContactId
    });
    return processResults(result);
}

export async function updateBank(paymentMethod) {
    const result = await makeSecureApiCall(`${BANK_URL}/${paymentMethod.id}`, 'PATCH', paymentMethod.LawPay);
    return processResults(result);
}

export async function updateCard(paymentMethod) {
    const result = await makeSecureApiCall(`${CARD_URL}/${paymentMethod.id}`, 'PATCH', paymentMethod.LawPay);
    return processResults(result);
}

export async function removeBank(paymentMethod) {
    const result = await makeSecureApiCall(`${BANK_URL}/${paymentMethod.id}`, 'DELETE', {});
    return processResults(result);
}

export async function removeCard(paymentMethod) {
    const result = await makeSecureApiCall(`${CARD_URL}/${paymentMethod.id}`, 'DELETE', {});
    return processResults(result);
}

export async function getBank(paymentMethod) {
    const result = await makeSecureApiCall(`${BANK_URL}/${paymentMethod.id}`, 'GET', {});
    return processResults(result);
}

export async function getCard(paymentMethod) {
    const result = await makeSecureApiCall(`${CARD_URL}/${paymentMethod.id}`, 'GET', {});
    return processResults(result);
}
*/
